import React from 'react';
import { Badge } from './ui/badge';
import { Check, Heart, Briefcase, Crown } from 'lucide-react';

/**
 * CrafterBadge - Displays the crafter type and subscription badge
 * 
 * @param {string} crafterType - 'hobbista' or 'professionista'
 * @param {string} tier - 'free' or 'pro'
 * @param {string} piva - P.IVA number (only for artigiano)
 * @param {string} size - 'sm', 'md', 'lg'
 * @param {boolean} showDetails - Show full badge with details
 */
export const CrafterBadge = ({ 
  crafterType = 'hobbista', 
  tier = 'free', 
  piva = null,
  size = 'md',
  showDetails = true 
}) => {
  const isHobbista = crafterType === 'hobbista';
  const isPro = tier === 'pro';

  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  if (isHobbista) {
    return (
      <div className="flex items-center gap-2">
        <Badge 
          className={`${sizeClasses[size]} bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border border-emerald-200`}
        >
          <span className="mr-1">🟢</span>
          Hobbista Italia
        </Badge>
        {isPro && (
          <Badge 
            className={`${sizeClasses[size]} bg-gradient-to-r from-[#DAA520] to-[#B8860B] text-white border-0`}
          >
            <Crown className={`${iconSizes[size]} mr-1`} />
            PRO
          </Badge>
        )}
      </div>
    );
  }

  // Artigiano
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Badge 
        className={`${sizeClasses[size]} bg-red-100 text-red-700 hover:bg-red-100 border border-red-200`}
      >
        <span className="mr-1">🔴</span>
        Artigiano P.IVA
        {piva && showDetails && (
          <span className="ml-1 opacity-75">#{piva.slice(-4)}</span>
        )}
      </Badge>
      {piva && (
        <Badge 
          className={`${sizeClasses[size]} bg-green-100 text-green-700 hover:bg-green-100 border border-green-200`}
        >
          <Check className={`${iconSizes[size]} mr-1`} />
          Verificato
        </Badge>
      )}
      {isPro && (
        <Badge 
          className={`${sizeClasses[size]} bg-gradient-to-r from-[#DAA520] to-[#B8860B] text-white border-0`}
        >
          <Crown className={`${iconSizes[size]} mr-1`} />
          PRO
        </Badge>
      )}
    </div>
  );
};

/**
 * CrafterTypeIcon - Small icon indicator for crafter type
 */
export const CrafterTypeIcon = ({ crafterType, size = 'md' }) => {
  const isHobbista = crafterType === 'hobbista';
  
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  if (isHobbista) {
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-emerald-100 flex items-center justify-center`}>
        <Heart className="w-3 h-3 text-emerald-600" />
      </div>
    );
  }

  return (
    <div className={`${sizeClasses[size]} rounded-full bg-red-100 flex items-center justify-center`}>
      <Briefcase className="w-3 h-3 text-red-600" />
    </div>
  );
};

/**
 * Validate Italian P.IVA format
 * Italian P.IVA is 11 digits
 */
export const validatePIVA = (piva) => {
  if (!piva) return { valid: false, error: 'P.IVA richiesta' };
  
  // Rimuove spazi, punti, trattini e l'eventuale prefisso paese "IT" (come nel placeholder)
  const cleaned = String(piva).replace(/[\s.\-]/g, '').replace(/^IT/i, '');
  
  // Check if it's 11 digits
  if (!/^\d{11}$/.test(cleaned)) {
    return { valid: false, error: 'La P.IVA deve contenere 11 cifre' };
  }

  // Reject trivially invalid sequences (all same digit, e.g. all zeros)
  if (/^(\d)\1{10}$/.test(cleaned)) {
    return { valid: false, error: 'P.IVA non valida' };
  }

  // Luhn-like checksum validation for Italian P.IVA
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    let digit = parseInt(cleaned[i], 10);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }

  if (sum % 10 !== 0) {
    return { valid: false, error: 'P.IVA non valida (checksum errato)' };
  }

  return { valid: true, cleaned };
};

/**
 * Format P.IVA for display
 */
export const formatPIVA = (piva) => {
  if (!piva) return '';
  const cleaned = String(piva).replace(/[\s.\-]/g, '').replace(/^IT/i, '');
  // Format as: 123 456 789 01
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1 $2 $3 $4');
};

export default CrafterBadge;
